import React from 'react'
import { css, cx } from '@emotion/css'
import {
  theme,
  ThemeColors
} from '@contrast-security-inc/design-system-foundations'

const borderRadius = 4

export const getThermometerCss = (color: ThemeColors, height?: number) => {
  const thermometerHeight = !!height ? height : 8
  return css`
    display: flex;
    justify-content: start;
    border-radius: ${borderRadius}px;
    overflow: hidden;
    width: 100%;
    height: ${thermometerHeight}px;
    background-color: ${theme.color[color]};
  `
}

const intervalCss = css`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
`

/**
 * If and when we need to switch theme out at runtime, we'll need a more robust
 * cache.
 */
const intervalCssCache = new Map<string, string>()

function getIntervalCss(color: ThemeColors) {
  if (!intervalCssCache.has(color)) {
    intervalCssCache.set(
      color,
      cx(
        intervalCss,
        css`
          background-color: ${theme.color[color]};
          border-radius: ${borderRadius}px;
        `
      )
    )
  }

  return intervalCssCache.get(color)
}

interface ValueRendererProps {
  /**
   * inline style that must be applied to the outer element returned by the
   * render prop
   */
  readonly width: string

  /**
   * The className which must be applied to the outer element returned by the
   * render prop
   */
  readonly className: string
}

interface Props {
  readonly progress: number
  readonly maxProgress: number

  readonly fillColor: ThemeColors

  readonly bgColor: ThemeColors

  /**
   * Render callback for the progress.
   */
  readonly renderProgress: (
    props: ValueRendererProps
  ) => React.ReactElement<any>

  readonly height?: number
}

export function ProgressThermometer({
  maxProgress,
  progress = 0,
  fillColor = 'green50',
  bgColor = 'neutral90',
  renderProgress,
  height
}: Props) {
  return (
    <div
      className={cx(
        'legacy-ds-progress-thermometer',
        getThermometerCss(bgColor, height)
      )}
    >
      {maxProgress > 0 &&
        maxProgress >= progress &&
        renderProgress({
          width: `${(progress / maxProgress) * 100}%`,
          className: getIntervalCss(fillColor) || ''
        })}
    </div>
  )
}
